import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import { useTranslation } from "react-i18next";
import {
  useAddQuestionToLearningTrackMutation,
  useUpdateLearningTrackQuestionMutation,
} from "../../services/organization/adminLearningTrack";

const getNextTempId = () =>
  Math.max(1, Math.floor(Math.random() * 100000) + Date.now());

/** Next sequential answer id for create flow: 1, 2, 3, 4, ... */
const getNextAnswerId = (answers) => {
  if (!answers?.length) return 1;
  const max = Math.max(...answers.map((a) => (typeof a.id === "number" ? a.id : 0)));
  return max + 1;
};

function normalizeInitialQuestion(initialQuestion) {
  if (!initialQuestion) return null;
  const questionText = initialQuestion.question ?? initialQuestion.title ?? "";
  const rawAnswers = Array.isArray(initialQuestion.answers)
    ? initialQuestion.answers
    : [];
  const answers = rawAnswers.map((a) => ({
    id: a.id ?? getNextTempId(),
    answer: a.answer ?? a.title ?? "",
    is_correct: a.is_correct ?? a.isCorrect ?? false,
  }));
  return {
    id: initialQuestion.id,
    question: questionText,
    answers:
      answers.length > 0
        ? answers
        : [{ id: getNextTempId(), answer: "", is_correct: false }],
  };
}

export default function AddQuestionModal({
  id,
  isOpened,
  onClose,
  learningTrackId,
  onSuccess,
  initialQuestion,
}) {
  const { t, i18n } = useTranslation(["learningPaths", "common"]);
  const answersDir = i18n.language === "ar" ? "rtl" : "ltr";
  const [addQuestion, { isLoading: isAddLoading }] =
    useAddQuestionToLearningTrackMutation();
  const [updateQuestion, { isLoading: isUpdateLoading }] =
    useUpdateLearningTrackQuestionMutation();

  const [question, setQuestion] = useState("");
  const [answers, setAnswers] = useState(
    Array.from({ length: 4 }, (_, i) => ({
      id: i + 1,
      answer: "",
      is_correct: false,
    }))
  );
  const [error, setError] = useState("");

  const isEditMode = Boolean(initialQuestion);
  const normalized = normalizeInitialQuestion(initialQuestion);

  useEffect(() => {
    const el = document.getElementById(id);
    if (!el) return;
    const bootstrapModal = window.bootstrap?.Modal?.getOrCreateInstance(el);
    if (!bootstrapModal) return;
    if (isOpened) {
      bootstrapModal.show();
    } else {
      bootstrapModal.hide();
    }
  }, [isOpened, id]);

  useEffect(() => {
    const el = document.getElementById(id);
    if (!el) return;
    const handleHidden = () => onClose?.();
    el.addEventListener("hidden.bs.modal", handleHidden);
    return () => el.removeEventListener("hidden.bs.modal", handleHidden);
  }, [id, onClose]);

  useEffect(() => {
    if (!isOpened) return;
    setError("");
    if (normalized) {
      setQuestion(normalized.question);
      const initialAnswers =
        normalized.answers.length > 0
          ? normalized.answers
          : [{ id: getNextTempId(), answer: "", is_correct: false }];
      while (initialAnswers.length < 2) {
        initialAnswers.push({
          id: getNextTempId(),
          answer: "",
          is_correct: false,
        });
      }
      setAnswers(initialAnswers);
    } else {
      setQuestion("");
      setAnswers(
        Array.from({ length: 4 }, (_, i) => ({
          id: i + 1,
          answer: "",
          is_correct: false,
        }))
      );
    }
  }, [isOpened, initialQuestion?.id]);

  const handleAddAnswer = () => {
    setAnswers((prev) => [
      ...prev,
      { id: getNextAnswerId(prev), answer: "", is_correct: false },
    ]);
  };

  const handleDeleteAnswer = (index) => {
    if (answers.length <= 2) return;
    setAnswers((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((a, i) => ({ ...a, id: i + 1 }))
    );
  };

  const handleAnswerChange = (index, value) => {
    setAnswers((prev) =>
      prev.map((a, i) => (i === index ? { ...a, answer: value } : a))
    );
  };

  const handleCorrectChange = (index) => {
    setAnswers((prev) =>
      prev.map((a, i) => ({ ...a, is_correct: i === index }))
    );
  };

  const handleSubmit = async () => {
    setError("");
    const trimmedQuestion = question?.trim();
    if (!trimmedQuestion) {
      setError(t("learningPaths:questionRequired"));
      return;
    }
    const validAnswers = answers
      .map((a) => ({ ...a, answer: (a.answer ?? "").trim() }))
      .filter((a) => a.answer !== "");
    if (validAnswers.length < 2) {
      setError(t("learningPaths:atLeastTwoAnswersRequired"));
      return;
    }
    const hasCorrect = validAnswers.some((a) => a.is_correct);
    if (!hasCorrect) {
      setError(t("learningPaths:pleaseSelectCorrectAnswer"));
      return;
    }

    const payload = {
      question: trimmedQuestion,
      answers: validAnswers.map((a) => ({
        id: a.id,
        title: a.answer,
        isCorrect: a.is_correct,
      })),
      archived: isEditMode ? (initialQuestion?.archived ?? false) : false,
    };

    try {
      if (isEditMode && normalized?.id) {
        const result = await updateQuestion({
          learningTrackId,
          questionId: normalized.id,
          body: payload,
        }).unwrap();
        if (result) {
          onSuccess?.();
          onClose?.();
        }
      } else {
        const result = await addQuestion({
          learningTrackId,
          body: payload,
        }).unwrap();
        if (result) {
          onSuccess?.();
          onClose?.();
        }
      }
    } catch (err) {
      setError(err?.data?.message || t("learningPaths:somethingWentWrong"));
    }
  };

  const modalTitle = isEditMode
    ? t("learningPaths:editQuestionModalTitle")
    : t("learningPaths:addQuestionModalTitle");
  const submitLabel = isEditMode
    ? t("learningPaths:updateQuestion")
    : t("learningPaths:saveQuestion");
  const isLoading = isAddLoading || isUpdateLoading;

  return (
    <Modal
      id={id}
      title={modalTitle}
      isOpened={isOpened}
      onClose={onClose}
      hasCloseBtn
      mainBtn={isLoading ? t("common:Loading") : submitLabel}
      mainBtnOnclick={isLoading ? undefined : handleSubmit}
      maxWidth={560}
      isVerticallyCentered
    >
      <div className="question-form-modal">
        <div className="mb-3">
          <label className="form-label font_16 font-extrabold">
            {t("learningPaths:questionLabel")}
          </label>
          <textarea
            className="form-control"
            rows={3}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={t("learningPaths:questionLabel")}
          />
        </div>

        <div className="question-form-modal__answers mb-3" dir={answersDir}>
          <label className="question-form-modal__answers-title form-label font_16 font-extrabold">
            {t("learningPaths:answersToQuestion")}
          </label>
          {answers.map((a, i) => (
            <div key={a.id} className="question-form-modal__answer-row">
              <span className="question-form-modal__answer-num">{i + 1}</span>
              <input
                type="text"
                className="question-form-modal__answer-input form-control"
                value={a.answer}
                onChange={(e) => handleAnswerChange(i, e.target.value)}
                placeholder={`${t("learningPaths:answersLabel")} ${i + 1}`}
              />
              <button
                type="button"
                className="question-form-modal__answer-delete btn"
                onClick={() => handleDeleteAnswer(i)}
                disabled={answers.length <= 2}
                aria-label={t("learningPaths:delete")}
              >
                <i className="fas fa-trash-alt" />
              </button>
              <button
                type="button"
                role="button"
                className={`question-form-modal__correct-btn ${
                  a.is_correct
                    ? "question-form-modal__correct-btn--selected"
                    : ""
                }`}
                onClick={() => handleCorrectChange(i)}
              >
                <i
                  className={`fa-fw ${
                    a.is_correct ? "fas fa-check" : "far fa-circle"
                  }`}
                  aria-hidden
                />
                <span>{t("learningPaths:correctAnswer")}</span>
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm mt-2"
            onClick={handleAddAnswer}
          >
            <i className="fas fa-plus me-2" />
            {t("learningPaths:addAnswer")}
          </button>
        </div>

        {error && <p className="text-danger font_14 mb-3">{error}</p>}
      </div>
    </Modal>
  );
}
